import Box from '@risk-smart/themed-cloudscape-components/box';
import ErrorContent from '@risksmart-app/components/src/error-pages/ErrorContent';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { OrganisationResponseRow } from '@risksmart-app/drizzle/src/types';
import { isE2eTestOrg } from '@risksmart-app/feature-flags/src/e2e-test-org';
import { parseIgnoreE2eOrgOverride } from '@risksmart-app/feature-flags/src/ignore-e2e-org-override';
import { parseOrgFeatures } from '@risksmart-app/modules/src/index';
import type { Meta } from '@risksmart-app/shared/organisation/Meta';
import { type FC, type ReactNode, useMemo } from 'react';
import Loading from 'src/components/loading';

import { E2eFlagOverridesContext } from './E2eFlagOverridesContext';
import { FeaturesContext } from './FeaturesContext';

export type FeaturesProviderOrganisation = Partial<
  Pick<OrganisationResponseRow, 'Meta'>
>;

const readFeatureMeta = (value: unknown): Meta | undefined => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }

  const features =
    'features' in value && typeof value.features === 'string'
      ? value.features
      : undefined;
  const isE2eTestOrg =
    'isE2eTestOrg' in value &&
    (typeof value.isE2eTestOrg === 'boolean' ||
      typeof value.isE2eTestOrg === 'string')
      ? value.isE2eTestOrg
      : undefined;

  return { features, isE2eTestOrg };
};

export const FeaturesProviderView: FC<{
  children: ReactNode;
  organisation: FeaturesProviderOrganisation | null | undefined;
  hasError: boolean;
  isLoading: boolean;
}> = ({ children, organisation, isLoading, hasError }) => {
  const meta = readFeatureMeta(organisation?.Meta);
  const features = useMemo(
    () => (meta?.features ? parseOrgFeatures(meta.features) : []),
    [meta?.features]
  );
  const overrideIgnored = parseIgnoreE2eOrgOverride(
    getEnv('REACT_APP_FEATURE_FLAG_IGNORE_E2E_ORG_OVERRIDE', true)
  );
  const e2eFlagOverrides =
    isE2eTestOrg(meta) && !overrideIgnored ? features : null;

  return (
    <FeaturesContext.Provider value={features}>
      <E2eFlagOverridesContext.Provider value={e2eFlagOverrides}>
        {isLoading && <Loading data-loading-reason={'features'} />}
        {hasError && (
          <ErrorContent
            title={'Sorry, we couldn’t make that happen'}
            imgSrc={'/errors/rubiks-cube.png'}
            imgAlt={'binoculars'}
          >
            <Box variant={'p'}>{'Please try again later'}</Box>
          </ErrorContent>
        )}
        {!hasError && !isLoading && children}
      </E2eFlagOverridesContext.Provider>
    </FeaturesContext.Provider>
  );
};
